/** Active-organization context and API-backed tenant switching control. */
import { useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useAuth } from "../../auth";

function roleLabel(role: string): string {
	return role
		.toLowerCase()
		.split("_")
		.map((part) =>
			part.length > 0 ? `${part[0]!.toUpperCase()}${part.slice(1)}` : part
		)
		.join(" ");
}

interface TenantSwitcherProps {
	compact?: boolean;
}

export function TenantSwitcher({ compact = false }: TenantSwitcherProps) {
	const { status, activeOrganization, memberships, actions } = useAuth();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	if (status !== "authenticated" || !activeOrganization) return null;

	const open = Boolean(anchorEl);
	const activeId = activeOrganization.id;
	const canSwitch = memberships.length >= 2;
	const label = activeOrganization.name;

	function close() {
		setAnchorEl(null);
	}

	async function handleSelect(organizationId: string) {
		if (organizationId === activeId) {
			close();
			return;
		}

		setPending(true);
		setError(null);
		try {
			await actions.switchOrganization(organizationId);
			close();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : "Failed to switch organization.");
		} finally {
			setPending(false);
		}
	}

	if (!canSwitch) {
		return (
			<Tooltip title={`Active organization: ${label}`}>
				<Chip
					icon={<BusinessIcon />}
					label={label}
					aria-label={`Active organization: ${label}`}
					variant="outlined"
					sx={{
						maxWidth: compact ? 92 : 210,
						color: "workspace.textSecondary",
						borderColor: "workspace.borderStrong",
						"& .MuiChip-icon": { color: "workspace.blue" },
						"& .MuiChip-label": {
							display: "block",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						},
					}}
				/>
			</Tooltip>
		);
	}

	return (
		<>
			<Tooltip title="Switch organization">
				<Button
					onClick={(event) => setAnchorEl(event.currentTarget)}
					color="inherit"
					size="small"
					variant="outlined"
					aria-haspopup="menu"
					aria-controls={open ? "organization-switcher-menu" : undefined}
					aria-expanded={open ? "true" : undefined}
					aria-label={`Switch organization. Active organization: ${label}`}
					startIcon={<BusinessIcon fontSize="small" />}
					endIcon={
						pending ? (
							<CircularProgress size={14} sx={{ color: "inherit" }} />
						) : (
							<ExpandMoreIcon fontSize="small" />
						)
					}
					disabled={pending}
					sx={{
						textTransform: "none",
						borderColor: "workspace.borderStrong",
						color: "workspace.textSecondary",
						maxWidth: compact ? 98 : { sm: 180, lg: 230 },
						minWidth: 0,
						px: compact ? 0.75 : 1.25,
						"&:hover": {
							borderColor: "workspace.blue",
							backgroundColor: "rgba(74,156,197,0.08)",
						},
						"& .MuiButton-startIcon, & .MuiButton-endIcon": {
							marginLeft: 0,
							marginRight: compact ? 0 : 0.5,
						},
					}}
				>
					<Typography variant="body2" noWrap sx={{ fontWeight: 650, color: "inherit" }}>
						{label}
					</Typography>
				</Button>
			</Tooltip>

			<Menu
				id="organization-switcher-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={close}
				keepMounted
				MenuListProps={{ "aria-label": "Available organizations" }}
			>
				{error && (
					<Box role="alert" sx={{ px: 2, py: 1, color: "error.main", maxWidth: 320 }}>
						<Typography variant="caption">{error}</Typography>
					</Box>
				)}
				{memberships.map((membership) => {
					const isActive = membership.organizationId === activeId;
					const name = membership.organization?.name ?? membership.organizationId;
					return (
						<MenuItem
							key={membership.id}
							onClick={() => void handleSelect(membership.organizationId)}
							selected={isActive}
							disabled={pending}
						>
							<ListItemIcon>
								{isActive ? <CheckIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
							</ListItemIcon>
							<ListItemText
								primary={
									<Stack direction="row" spacing={1} alignItems="center">
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
											{name}
										</Typography>
										<Chip size="small" label={roleLabel(membership.role)} />
									</Stack>
								}
							/>
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
}
